import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Button } from 'ncoded-component-library';
import useGetExpirationDate from '../../hooks/useGetExpirationDate';
import utils from 'utils';

import './ExtendFileAccess.styles.scss';
import './ExtendFileAccess.styles.responsive.scss';

const RED_HOURS = 72 * 60;

type ExtendFileAccessProps = {
  className?: string;
};

const ExtendFileAccess: React.FC<ExtendFileAccessProps> = (props) => {
  const { className } = props;

  const {
    isBuySide,
    dataRoom: {
      _id,
      accessConfirmation,
      canRenewFileAccess,
      renewFileAccessDays,
      currentMember: {
        filesAccessExpiredAt,
        accessableDataRoomNodesView,
        canExtendAccess,
      },
    },
    setDataRoom,
  } = useContext(DataRoomContext);

  const classes = classNames('extend-file-access', className);

  const { t } = useTranslation();

  const canRenew =
    canRenewFileAccess && canExtendAccess && !!filesAccessExpiredAt;

  const expirationDate = useGetExpirationDate()(filesAccessExpiredAt);

  const [extendFileAccess, isLoading] = useCallbackAsync(async () => {
    const { data } = await dataRoomApi.members.extendFileAccess(_id);

    setDataRoom((old) => ({
      ...old,
      currentMember: { ...old.currentMember, ...data },
    }));
  });

  const isRed = useMemo(() => {
    const { min, getCount } = utils.getDateVariables(filesAccessExpiredAt);

    const minutes = getCount(min);

    return minutes < RED_HOURS;
  }, [filesAccessExpiredAt]);

  if (
    !isBuySide ||
    !expirationDate ||
    !accessConfirmation ||
    !accessableDataRoomNodesView.length
  )
    return null;

  return (
    <div className={classes}>
      <p className={classNames({ red: isRed })}>
        {t('fileAccessExpiresInDays', {
          time: expirationDate,
        })}
      </p>

      {canRenew && (
        <p>
          <Trans
            i18nKey={'extendAccess'}
            values={{
              count: renewFileAccessDays,
            }}
            components={{
              button: (
                <Button
                  variant="link"
                  disabled={isLoading}
                  onClick={extendFileAccess}
                >
                  {t('here')}
                </Button>
              ),
            }}
          />
        </p>
      )}
    </div>
  );
};

export default ExtendFileAccess;
